import { alpha, Box, CircularProgress, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { EldDriver, EldVehicle } from "src/models";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import { Constants } from "src/constants";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import TruxSelectField from "src/components/inputs/trux-select-field";
import Label from "src/components/label";
import { useDropzone } from "react-dropzone";
import axios, { endpoints } from "src/utils/axios";

type EldDriverModalProps = {
  selectedEldDriverModel: ModelSavingData<EldDriver>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateEldDriver: VoidFunction,
  eldVehicles: EldVehicle[],
}

export function EldDriverModal(
  { selectedEldDriverModel, isOpen, setIsOpen, updateEldDriver, eldVehicles }: EldDriverModalProps
) {

  const selectedAdditionalVehicles: string[] = selectedEldDriverModel.model?.eldVehicleIds.map(id => eldVehicles.find(v => v.id === id)).map(v => v?.vehicleUnitNumber).filter(v => v) as string[] | [];



  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => {
        setSavingFile(true)

        // here we need to upload the image
        axios.postForm(
          endpoints.eldDrivers.uploadSignature,
          { file }
        ).then(response => response.data).then((response) => {
          if (response.fileUrl) {
            enqueueSnackbar("Successfully uploaded the File", { variant: "success" });

            selectedEldDriverModel.update(eldDriver => {
              eldDriver.signatureUrl = response.fileUrl;
            });
          }
        }).catch(() => {
          enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
        }).finally(() => {
          setSavingFile(false);
        })
      })
    },
    [selectedEldDriverModel]
  );

  const onRemoveFile = useCallback(() => {
    selectedEldDriverModel.update(eldDriver => {
      eldDriver.signatureUrl = "";
    })
  }, [selectedEldDriverModel]);

  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop, accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    }
  })


  return selectedEldDriverModel.model && <SimpleDialog
    title={<>{selectedEldDriverModel.isNew ? "New Eld Driver" : "Update Eld Driver"} {!selectedEldDriverModel.model?.signatureUrl && <Label variant="filled" color="error">No Signature</Label>}</>}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={updateEldDriver}
    submitDisabled={selectedEldDriverModel.loading}
    submitButton={selectedEldDriverModel.isNew ? "New Eld Driver" : "Update Eld Driver"}
    submitButtonIcon={selectedEldDriverModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
    submitButtonColor={selectedEldDriverModel.isNew ? "success" : "info"}>


    <DisplayErrors savingData={selectedEldDriverModel} />

    <Stack direction="column" gap={1}>

      <TruxFormField label="Username *">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="username" />
      </TruxFormField>
      <TruxFormField label={`Password${!selectedEldDriverModel.model?.id ? " *" : ""}`}>
        <TruxTextField modelSavingData={selectedEldDriverModel} field="newPassword" />
        {!!selectedEldDriverModel.model?.id && <Typography fontSize="small" fontStyle="italic">
          * if left blank will not update the password
        </Typography>}
      </TruxFormField>
      {(selectedEldDriverModel.model?.newPassword || "").length > 0 && <TruxFormField label={`Confirm Password${!selectedEldDriverModel.model?.id ? " *" : ""}`}>
        <TruxTextField modelSavingData={selectedEldDriverModel} field="newPasswordConfirm" />
        {selectedEldDriverModel.model?.newPassword !== selectedEldDriverModel.model?.newPasswordConfirm && <Typography fontSize="small" fontStyle="italic" color="error">
          * passwords do not match
        </Typography>}
      </TruxFormField>}

      <TruxFormField label="First Name *">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="firstName" />
      </TruxFormField>
      <TruxFormField label="Last Name *">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="lastName" />
      </TruxFormField>



      <TruxFormField label="All Vehicles">
        <Box display="flex" alignItems="center">
          <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="hasAllVehicles" />
          <Typography fontSize="small" fontStyle="italic">
            * if checked the driver will be able to use all trucks
          </Typography>
        </Box>
      </TruxFormField>

      <TruxFormField label="Main Vehicle">
        <TruxAutocompleteField
          options={[...eldVehicles.filter(vehicle => vehicle.active).map(vehicle => ({
            label: `${vehicle.vehicleUnitNumber}`,
            id: vehicle.id
          })), {
            label: "None",
            id: 0
          }]}
          onClear={() => {
            selectedEldDriverModel.update(eldDriver => {
              eldDriver.eldVehicleId = 0;
              eldDriver.eldVehicleUnitNumber = "";
              eldDriver.eldVehicleVin = "";
            })
          }}
          onChange={(value) => {
            selectedEldDriverModel.update(eldDriver => {
              const truckId = value === null ? eldDriver.eldVehicleId : Number(value?.id);
              eldDriver.eldVehicleId = truckId;
              eldVehicles.forEach(eldVehicle => {
                if (eldVehicle.id === eldDriver.eldVehicleId) {
                  eldDriver.eldVehicleUnitNumber = eldVehicle.vehicleUnitNumber;
                  eldDriver.eldVehicleVin = eldVehicle.vin;
                }
              })
              if (eldDriver.eldVehicleId === 0) {
                eldDriver.eldVehicleUnitNumber = "";
                eldDriver.eldVehicleVin = "";
              }
            })
          }}
          selected={{
            id: selectedEldDriverModel.model.eldVehicleId,
            label: selectedEldDriverModel.model.eldVehicleId === 0 ? "None" : `${selectedEldDriverModel.model.eldVehicleUnitNumber}`,
          }}
        />
      </TruxFormField>

      {!selectedEldDriverModel.model?.hasAllVehicles && <TruxFormField label="Additional Vehicles">
        <Select
          sx={{minWidth: "100%"}}
          multiple
          value={selectedAdditionalVehicles}
          onChange={(e) => {
            const selected = e.target.value;
            if (selected.includes("Clear All")) {
              selectedEldDriverModel.update(eldDriver => {
                eldDriver.eldVehicleIds = [];
              })
              return;
            }
            const vehicles = eldVehicles.filter(v => selected.includes(v.vehicleUnitNumber));

            selectedEldDriverModel.update(eldDriver => {
              eldDriver.eldVehicleIds = vehicles.map(v => v.id);
            })
          }}
          MenuProps={MenuProps}
        >
          <MenuItem
            value="Clear All"
          >
            Clear All
          </MenuItem>
          {eldVehicles.map((vehicle) => (
            <MenuItem
              key={vehicle.vehicleUnitNumber}
              value={vehicle.vehicleUnitNumber}
            >
              {vehicle.vehicleUnitNumber}
            </MenuItem>
          ))}
        </Select>
      </TruxFormField>
      }

      <TruxFormField label="Driver License *">
        <Box display="flex" gap={1}>
          <Box>
            <TruxTextField modelSavingData={selectedEldDriverModel} field="driverLicenseNumber" />
          </Box>

          <Box>
            <TruxSelectField  modelSavingData={selectedEldDriverModel} field="driverLicenseState" >
              <MenuItem value="" selected={!!selectedEldDriverModel.model?.driverLicenseState}>Please Select</MenuItem>
              {Constants.STATES.map((state) => <MenuItem value={state} key={state} selected={state === selectedEldDriverModel.model?.driverLicenseState}>{state}</MenuItem>)}
            </TruxSelectField>
          </Box>
        </Box>
      </TruxFormField>


      <TruxFormField label="Driver Number">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="driverNumber" />
      </TruxFormField>

      <TruxFormField label="Phone">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="phone" />
      </TruxFormField>

      {/* <TruxFormField label="Email">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="email" />
      </TruxFormField> */}

      {/* <TruxFormField label="Exempt Driver">
        <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="exemptDriver" />
      </TruxFormField>
      <TruxFormField label="Hos Rule">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="hosRule" />
      </TruxFormField>
      <TruxFormField label="Cargo Type">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="cargoType" />
      </TruxFormField>
      <TruxFormField label="Restart">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="restart" />
      </TruxFormField>
      <TruxFormField label="Rest Break">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="restBreak" />
      </TruxFormField> */}
      <TruxFormField label="Allow Personal Use">
        <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="allowPersonalUse" />
      </TruxFormField>
      <TruxFormField label="Allow Yard Moves">
        <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="allowYardMoves" />
      </TruxFormField>
      <TruxFormField label={
        <Box display="flex" alignItems="center" gap={0.75}>
          Allow Short SB
          <Tooltip title="Lets the driver mark a rest as the short part of a sleeper berth split — a break of at least 2 hours that pairs with a later sleeper berth of 7 or more hours — so it is immediately excluded from their 14-hour shift window." arrow>
            <Iconify width={16} icon="eva:info-outline" sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Box>
      }>
        <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="canUseShortSbFirst" />
      </TruxFormField>
      {!selectedEldDriverModel.isNew &&
        <TruxFormField label="Active">
          <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="active" />
        </TruxFormField>
      }
      {!selectedEldDriverModel.isNew &&
        <TruxFormField label="Suspended">
          <TruxCheckboxField modelSavingData={selectedEldDriverModel} field="suspended" />
        </TruxFormField>
      }

      {/* <TruxFormField label="Unlimited Trailers">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="unlimitedTrailers" />
      </TruxFormField>
      <TruxFormField label="Unlimited ShippingDocs">
        <TruxTextField modelSavingData={selectedEldDriverModel} field="unlimitedShippingDocs" />
      </TruxFormField> */}


      <TruxFormField label="Signature Image">
        <>
          {savingFile && <CircularProgress />}
          {!savingFile && selectedEldDriverModel.model?.signatureUrl && <Stack direction="row" gap={2}>
            <Box maxWidth="200px">
              <img src={selectedEldDriverModel.model?.signatureUrl} alt="Invoice Logo" />
            </Box>
            <Box>
              <IconButton color="error" onClick={onRemoveFile} >
                <Iconify icon="solar:trash-bin-trash-bold" />
              </IconButton>
            </Box>
          </Stack>}
          {!savingFile && !selectedEldDriverModel.model?.signatureUrl && <Box>
            <Box {...getRootProps()} onFocus={null as any} onBlur={null as any}>
              <input {...getInputProps()} />

              <Box sx={{
                m: 0.5,
                flexShrink: 0,
                flexGrow: 1,
                height: "80px",
                width: "120px",
                borderRadius: 1,
                cursor: 'pointer',
                alignItems: 'center',
                color: 'text.disabled',
                justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                border: (theme) => `dashed 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                ...(isDragActive && {
                  opacity: 0.72,
                }),
                '&:hover': {
                  opacity: 0.72,
                }
              }}><Stack spacing={0.5} paddingTop={1} alignItems="center">
                  <Iconify icon="eva:cloud-upload-fill" width={40} />
                  <Typography variant="body2">Upload File</Typography>
                </Stack></Box>
            </Box>
          </Box>}
        </>
      </TruxFormField>
    </Stack>
  </SimpleDialog >
}


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
